import React from 'react'
import { Card, Tag, Space, Button, Typography } from 'antd'
import { EyeOutlined, ClockCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

const { Text } = Typography

export interface TradeRecordCardData {
  id: number
  symbol: string
  side: string
  status: string
  contracts: number
  entry_price: number
  exit_price?: number | null
  position_value_usdt?: number | null
  opened_at: string
  closed_at?: string | null
  holding_hours?: number
  realized_pnl: number
  net_pnl: number
  commission?: number
  exchange: string
  token_name?: string
}

interface TradeRecordCardProps {
  record: TradeRecordCardData
  getSideMeta: (side: unknown, status?: unknown) => { color: string; label: string }
  formatPrice: (value: unknown) => string
  formatCurrency: (value: number) => string
  formatDuration: (hours: number) => string
  getPnlColor: (pnl: number) => string
  onViewDetail: (record: TradeRecordCardData) => void
}

const statusMeta: Record<string, { color: string; label: string }> = {
  open: { color: 'processing', label: '持仓中' },
  closed: { color: 'success', label: '已平仓' },
  liquidated: { color: 'error', label: '已清算' },
  unmatched: { color: 'warning', label: '未配对' },
}

const TradeRecordCard: React.FC<TradeRecordCardProps> = ({
  record,
  getSideMeta,
  formatPrice,
  formatCurrency,
  formatDuration,
  getPnlColor,
  onViewDetail,
}) => {
  const sideMeta = getSideMeta(record.side, record.status)
  const st = statusMeta[String(record.status || '').toLowerCase()] || {
    color: 'default',
    label: String(record.status || '-'),
  }
  const pnl = Number(record.net_pnl) || 0
  const rowClass =
    record.status === 'liquidated'
      ? 'trade-card liquidated'
      : pnl > 0
        ? 'trade-card profit'
        : pnl < 0
          ? 'trade-card loss'
          : 'trade-card'

  return (
    <Card
      size="small"
      className={rowClass}
      onClick={() => onViewDetail(record)}
      styles={{ body: { padding: '12px 14px' } }}
    >
      <div className="trade-card-header">
        <Space wrap size={4}>
          <Tag color="blue" style={{ margin: 0, fontFamily: 'monospace' }}>
            {record.symbol}
          </Tag>
          <Tag color={sideMeta.color}>{sideMeta.label}</Tag>
          <Tag color={st.color}>{st.label}</Tag>
        </Space>
        <Text strong style={{ color: getPnlColor(pnl), fontSize: 15 }}>
          {formatCurrency(pnl)}
        </Text>
      </div>

      <div className="trade-card-grid">
        <div>
          <Text type="secondary" className="trade-card-label">
            数量
          </Text>
          <div>{Number(record.contracts).toFixed(4)}</div>
        </div>
        <div>
          <Text type="secondary" className="trade-card-label">
            开仓价
          </Text>
          <div>{formatPrice(record.entry_price)}</div>
        </div>
        <div>
          <Text type="secondary" className="trade-card-label">
            平仓价
          </Text>
          <div>{formatPrice(record.exit_price)}</div>
        </div>
        <div>
          <Text type="secondary" className="trade-card-label">
            手续费
          </Text>
          <div>{formatCurrency(Number(record.commission) || 0)}</div>
        </div>
      </div>

      <div className="trade-card-footer">
        <Space size={4} wrap>
          <Tag color="purple" style={{ margin: 0 }}>
            {String(record.exchange || '').toUpperCase()}
          </Tag>
          {record.token_name ? (
            <Text type="secondary" ellipsis style={{ maxWidth: 120 }}>
              {record.token_name}
            </Text>
          ) : null}
        </Space>
        <Space size={8}>
          {record.holding_hours ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined /> {formatDuration(Number(record.holding_hours))}
            </Text>
          ) : null}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(record.opened_at).format('MM-DD HH:mm')}
          </Text>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              onViewDetail(record)
            }}
            style={{ padding: 0, height: 'auto' }}
          >
            详情
          </Button>
        </Space>
      </div>
    </Card>
  )
}

export default TradeRecordCard
